/**
 * 帮助中心文章管理 — GET/POST/PATCH/DELETE /api/admin/help
 */
import { useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Popconfirm, Switch, Table, Tag, message } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface HelpRow {
  id: number;
  category: string;
  title: string;
  body: string;
  sort: number;
  is_published: boolean;
  create_time: string;
}

interface HelpForm {
  category: string;
  title: string;
  body: string;
  sort: number;
  is_published: boolean;
}

export function HelpPage() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<HelpRow | null>(null);
  const [createForm] = Form.useForm<HelpForm>();
  const [editForm] = Form.useForm<HelpForm>();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canCreate = hasNode('platform.help.create');
  const canEdit = hasNode('platform.help.edit');
  const canDelete = hasNode('platform.help.delete');

  const { data = [], isFetching } = useQuery({
    queryKey: ['admin', 'help'],
    queryFn: () => api.get<HelpRow[]>('/api/admin/help'),
  });

  async function onCreate(values: HelpForm) {
    try {
      await api.post('/api/admin/help', values);
      message.success('文章已创建');
      setCreateOpen(false);
      createForm.resetFields();
      qc.invalidateQueries({ queryKey: ['admin', 'help'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '创建失败');
    }
  }

  async function onEdit(values: HelpForm) {
    if (!editRow) return;
    try {
      await api.patch(`/api/admin/help/${editRow.id}`, values);
      message.success('已更新');
      setEditRow(null);
      qc.invalidateQueries({ queryKey: ['admin', 'help'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '更新失败');
    }
  }

  async function onDelete(id: number) {
    try {
      await api.del(`/api/admin/help/${id}`);
      message.success('已删除');
      qc.invalidateQueries({ queryKey: ['admin', 'help'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '删除失败');
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '分类', dataIndex: 'category', width: 140, render: (v: string) => <Tag>{v}</Tag> },
    { title: '标题', dataIndex: 'title', ellipsis: true },
    { title: '排序', dataIndex: 'sort', width: 70 },
    {
      title: '状态',
      dataIndex: 'is_published',
      width: 90,
      render: (v: boolean) => (v ? <Tag color="green">已发布</Tag> : <Tag>草稿</Tag>),
    },
    { title: '创建时间', dataIndex: 'create_time', width: 170 },
    {
      title: '操作',
      key: 'op',
      width: 150,
      render: (_: unknown, row: HelpRow) => (
        <>
          {canEdit && (
            <Button
              type="link"
              size="small"
              onClick={() => {
                setEditRow(row);
                editForm.setFieldsValue({
                  category: row.category,
                  title: row.title,
                  body: row.body,
                  sort: row.sort,
                  is_published: row.is_published,
                });
              }}
            >
              编辑
            </Button>
          )}
          {canDelete && (
            <Popconfirm title="确定删除？" onConfirm={() => onDelete(row.id)}>
              <Button type="link" size="small" danger>
                删除
              </Button>
            </Popconfirm>
          )}
        </>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        {canCreate && (
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            新建文章
          </Button>
        )}
      </div>

      <Table<HelpRow>
        size="small"
        bordered
        rowKey="id"
        loading={isFetching}
        dataSource={data}
        columns={columns}
        pagination={false}
      />

      <Modal
        open={createOpen}
        title="新建帮助文章"
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        width={720}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          initialValues={{ sort: 0, is_published: true }}
          onFinish={onCreate}
        >
          <Form.Item name="category" label="分类" rules={[{ required: true, max: 64 }]}>
            <Input placeholder="例如：账户、充值、提现、EP" />
          </Form.Item>
          <Form.Item name="title" label="标题" rules={[{ required: true, max: 200 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="body" label="正文（支持 markdown）" rules={[{ required: true }]}>
            <Input.TextArea rows={10} />
          </Form.Item>
          <Form.Item name="sort" label="排序"><InputNumber /></Form.Item>
          <Form.Item name="is_published" label="立即发布" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>

      <Modal
        open={editRow !== null}
        title="编辑帮助文章"
        onCancel={() => setEditRow(null)}
        onOk={() => editForm.submit()}
        width={720}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={onEdit}>
          <Form.Item name="category" label="分类" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="title" label="标题" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="body" label="正文" rules={[{ required: true }]}>
            <Input.TextArea rows={10} />
          </Form.Item>
          <Form.Item name="sort" label="排序"><InputNumber /></Form.Item>
          <Form.Item name="is_published" label="发布" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
